import { FC, ReactNode, RefObject, useState } from 'react';
import { Button, Popover, Space, Typography } from 'antd';
import { TbDirection, TbInfoCircleFilled, TbMinus, TbPlus, TbRefresh } from 'react-icons/tb';
import type { MapRef } from 'react-map-gl/maplibre';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import SmallTooltip, { SmallTooltipContent } from '@/shared/components/tooltips/SmallTooltip';

import './index.css';

const MAP_CONTROL_ICON_SIZE = '1rem';
const MAP_CONTROL_ICON_SCALE = {
    direction: 1.25,
    refresh: 0.875,
} as const;

interface ThemedMapControlsProps {
    mapRef: RefObject<MapRef | null>;
    onReset: () => void;
    showDirection?: boolean;
}

const ThemedMapControls: FC<ThemedMapControlsProps> = ({ mapRef, onReset, showDirection = true }) => {
    const { token } = useAntDesignTheme();
    const [attributionOpen, setAttributionOpen] = useState(false);
    const buttonStyle = {
        alignItems: 'center',
        display: 'inline-flex',
        height: token.controlHeightSM,
        justifyContent: 'center',
        width: token.controlHeightSM,
    };
    const controlIcon = (icon: ReactNode, scale = 1) => (
        <span
            style={{
                alignItems: 'center',
                color: token.colorTextTertiary,
                display: 'inline-flex',
                height: '1rem',
                justifyContent: 'center',
                lineHeight: 0,
                transform: `scale(${scale})`,
                transformOrigin: 'center',
                width: '1rem',
            }}
        >
            {icon}
        </span>
    );
    const attributionContent = (
        <span
            style={{
                lineHeight: 1.5,
                maxWidth: 'min(22rem, calc(100vw - 2rem))',
                overflowWrap: 'anywhere',
                whiteSpace: 'normal',
            }}
        >
            <Typography.Link href='https://openfreemap.org' rel='noreferrer' target='_blank'>
                OpenFreeMap
            </Typography.Link>
            <Typography.Text style={{ color: token.colorTextTertiary }}> © </Typography.Text>
            <Typography.Link href='https://openmaptiles.org' rel='noreferrer' target='_blank'>
                OpenMapTiles
            </Typography.Link>
            <Typography.Text style={{ color: token.colorTextTertiary }}> data from </Typography.Text>
            <Typography.Link href='https://www.openstreetmap.org/copyright' rel='noreferrer' target='_blank'>
                OpenStreetMap
            </Typography.Link>
        </span>
    );

    return (
        <>
            <div
                style={{
                    borderRadius: token.borderRadius,
                    boxShadow: token.boxShadowTertiary,
                    position: 'absolute',
                    right: '0.75rem',
                    top: '0.75rem',
                    zIndex: 2,
                }}
            >
                <Space.Compact direction='vertical' size='small'>
                    <SmallTooltip placement='left' title='Zoom in'>
                        <Button
                            aria-label='Zoom in'
                            className='themed-map-control-button'
                            icon={controlIcon(<TbPlus size={MAP_CONTROL_ICON_SIZE} />)}
                            onClick={() => mapRef.current?.getMap().zoomIn({ duration: 300 })}
                            size='small'
                            style={buttonStyle}
                        />
                    </SmallTooltip>
                    <SmallTooltip placement='left' title='Zoom out'>
                        <Button
                            aria-label='Zoom out'
                            className='themed-map-control-button'
                            icon={controlIcon(<TbMinus size={MAP_CONTROL_ICON_SIZE} />)}
                            onClick={() => mapRef.current?.getMap().zoomOut({ duration: 300 })}
                            size='small'
                            style={buttonStyle}
                        />
                    </SmallTooltip>
                    {showDirection ? (
                        <SmallTooltip placement='left' title='Reset direction'>
                            <Button
                                aria-label='Reset direction'
                                className='themed-map-control-button'
                                icon={controlIcon(
                                    <TbDirection size={MAP_CONTROL_ICON_SIZE} />,
                                    MAP_CONTROL_ICON_SCALE.direction,
                                )}
                                onClick={() => mapRef.current?.getMap().resetNorthPitch({ duration: 300 })}
                                size='small'
                                style={buttonStyle}
                            />
                        </SmallTooltip>
                    ) : null}
                    <SmallTooltip placement='left' title='Reset map'>
                        <Button
                            aria-label='Reset map'
                            className='themed-map-control-button'
                            icon={controlIcon(
                                <TbRefresh size={MAP_CONTROL_ICON_SIZE} />,
                                MAP_CONTROL_ICON_SCALE.refresh,
                            )}
                            onClick={onReset}
                            size='small'
                            style={buttonStyle}
                        />
                    </SmallTooltip>
                </Space.Compact>
            </div>
            <div
                style={{
                    bottom: '0.75rem',
                    position: 'absolute',
                    right: '0.75rem',
                    zIndex: 2,
                }}
            >
                <SmallTooltip
                    disabled={attributionOpen}
                    overlayStyle={{
                        maxWidth: 'min(22rem, calc(100vw - 2rem))',
                    }}
                    placement='left'
                    title={attributionContent}
                >
                    <Popover
                        arrow={false}
                        content={<SmallTooltipContent>{attributionContent}</SmallTooltipContent>}
                        destroyOnHidden
                        onOpenChange={setAttributionOpen}
                        open={attributionOpen}
                        placement='left'
                        styles={{
                            body: {
                                padding: '0.25rem 0.5rem',
                            },
                            root: {
                                maxWidth: 'min(22rem, calc(100vw - 2rem))',
                            },
                        }}
                        trigger='click'
                    >
                        <Button
                            aria-label='Map attribution'
                            className='themed-map-control-button'
                            icon={controlIcon(<TbInfoCircleFilled size={MAP_CONTROL_ICON_SIZE} />)}
                            shape='circle'
                            size='small'
                            style={{
                                ...buttonStyle,
                                boxShadow: token.boxShadowTertiary,
                            }}
                        />
                    </Popover>
                </SmallTooltip>
            </div>
        </>
    );
};

export default ThemedMapControls;
